import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { DatePickerNew, Input, Select } from 'common-src/components/base';
import { booleanOptions } from 'common-src/constants/booleanOptions';
import PatientCaregiver, {
  CaregiverMedicareStatus,
  CaregiverMedicareStatusOptions,
  CaregiverPhoneTypeOptions,
  CaregiverPreferredLanguageOptions,
  CaregiverRelationshipOptions,
  CaregiverSexOptions,
  createPatientCaregiver,
  TechComfortLevelOptions,
  updatePatientCaregiver,
  validatePatientCaregiver,
} from 'common-src/models/PatientCaregiver';
import { getFormattedPhoneNumber } from 'common-src/models/PatientPhoneNumber/helpers';
import { cleanedText, PHONE_MASK } from 'common-src/presenters';
import { ColorsNew } from 'common-src/styles';

import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import styles from './CaregiverPopup.module.scss';
import { initialState } from './constants';
import { catchErrors } from './helpers';

const CaregiverPopup = ({ patientId, caregiver, hasActiveCaregivers, isGuide, onClose }) => {
  const dispatch = useDispatch();

  const [data, setData] = useState(initialState);

  const { errors, setErrors, autoFocus, setAutoFocus, renderButtons } = usePopup();

  useEffect(() => {
    const newData = caregiver ? _.pick(caregiver, Object.keys(initialState)) : { ...initialState };

    // Remove +1 prefix from phone if it exists
    if (newData.phone && newData.phone.startsWith('+1')) {
      newData.phone = newData.phone.substring(2);
    }

    // If this is the first caregiver, make them primary
    if (!hasActiveCaregivers && !caregiver) {
      newData.isPrimary = true;
    }

    setData(newData);
  }, [caregiver, hasActiveCaregivers]);

  const onChangeHandler = (field, value) => {
    setErrors((prev) => ({
      ...prev,
      ...validatePatientCaregiver(field, value, data, isGuide),
    }));
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const onSubmitHandler = (loadingCallback, successCallback, errorCallback) => {
    const { errors, hasErrors } = catchErrors(data, isGuide);
    if (hasErrors) {
      setErrors(errors);
      return;
    }

    loadingCallback();

    if (caregiver?.id) {
      dispatch(
        updatePatientCaregiver(
          { id: caregiver.id, ...data, phone: `+1${data.phone}` },
          {
            successBlock: () => {
              successCallback('Caregiver edited!');
              onClose(true);
            },
            errorBlock: (err) => errorCallback(err),
          },
        ),
      );

      return;
    }

    dispatch(
      createPatientCaregiver(
        { patientId, ...data },
        {
          successBlock: () => {
            successCallback('Caregiver added!');
            onClose(true);
          },
          errorBlock: (err) => errorCallback(err),
        },
      ),
    );
  };

  return (
    <BasePopup
      id="caregiver"
      open
      onClose={onClose}
      title={`${caregiver ? 'Edit' : 'Add'} Caregiver`}
      customStyle={{ minWidth: '65vw' }}
    >
      <div className={styles.grid}>
        <Input
          id="firstName"
          placeholder="Enter first name"
          label="First Name"
          value={data.firstName}
          onTextChange={(value) => onChangeHandler('firstName', value)}
          autoFocus={autoFocus}
          onBlur={() => setAutoFocus(false)}
          required
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.firstName}
        />
        <Input
          id="lastName"
          placeholder="Enter last name"
          label="Last Name"
          value={data.lastName}
          onTextChange={(value) => onChangeHandler('lastName', value)}
          onBlur={() => setAutoFocus(false)}
          required
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.lastName}
        />
        <Select
          id="is-primary"
          label="Is primary?"
          options={booleanOptions}
          onChange={(op) => onChangeHandler('isPrimary', op.value)}
          value={booleanOptions.find((option) => option.value === data.isPrimary)}
          placeholder="Is primary?"
          disabled={caregiver ? caregiver.isPrimary : !hasActiveCaregivers}
          required
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.isPrimary}
        />
        <Input
          id="email"
          classNames={[styles.width75]}
          label="Email address"
          placeholder="Enter email"
          onTextChange={(text) => onChangeHandler('email', text)}
          value={data.email}
          required
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.email}
        />
        <DatePickerNew
          id="date-of-birth"
          minDate={undefined}
          maxDate={new Date()}
          placeholder="Choose date of birth"
          header="Date of birth"
          value={data.dateOfBirth || null}
          onDateSelected={(date) =>
            onChangeHandler('dateOfBirth', date ? moment(date).format('YYYY-MM-DD') : null)
          }
          onBlur={(date) => {
            if (date === undefined) {
              setErrors((prev) => ({ ...prev, dateOfBirth: 'Invalid date format' }));
              return;
            }
            setErrors((prev) => ({ ...prev, dateOfBirth: '' }));
          }}
          required={isGuide}
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.dateOfBirth}
        />
        <Input
          id="phone"
          classNames={[styles.phoneInput]}
          label="Phone Number"
          onTextChange={(value) => onChangeHandler('phone', cleanedText(value))}
          value={data.phone ? getFormattedPhoneNumber(data.phone) : ''}
          mask={PHONE_MASK}
          placeholder="Enter phone number"
          required
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.phone}
        />
        <Select
          id="phoneType"
          label="Phone Type"
          options={CaregiverPhoneTypeOptions}
          onChange={(op) => onChangeHandler('phoneType', op.value)}
          value={CaregiverPhoneTypeOptions.find((op) => op.value === data.phoneType)}
          placeholder="Select phone type"
          required
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.phoneType}
        />
        <Select
          id="sex"
          label="Sex"
          options={CaregiverSexOptions}
          onChange={(op) => onChangeHandler('sex', op.value)}
          value={CaregiverSexOptions.find((op) => op.value === data.sex)}
          placeholder="Select sex"
          required={isGuide}
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.sex}
        />
        <Select
          id="relationship"
          label="Relationship"
          options={CaregiverRelationshipOptions}
          onChange={(op) => onChangeHandler('relationship', op.value)}
          value={CaregiverRelationshipOptions.find((op) => op.value === data.relationship)}
          placeholder="Select relationship"
          required
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.relationship}
        />
        <Select
          id="lives-with-patient"
          label="Lives with patient?"
          options={booleanOptions}
          onChange={(op) => onChangeHandler('livesWithPatient', op.value)}
          value={booleanOptions.find((option) => option.value === data.livesWithPatient)}
          placeholder="Lives with patient"
          required
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.livesWithPatient}
        />
        <Select
          id="medicare-status"
          label="Medicare status"
          options={CaregiverMedicareStatusOptions}
          onChange={(op) => {
            onChangeHandler('medicareStatus', op.value);
            if (op.value !== CaregiverMedicareStatus.Yes) {
              setErrors((prev) => ({ ...prev, mbi: '' }));
            }
          }}
          value={CaregiverMedicareStatusOptions.find((op) => op.value === data.medicareStatus)}
          placeholder="Medicare status"
          required={isGuide}
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.medicareStatus}
        />
        <Input
          id="mbi"
          label="MBI Number"
          onTextChange={(value) => onChangeHandler('mbi', value)}
          value={data.mbi}
          placeholder="Enter medicare number"
          required={data.medicareStatus === CaregiverMedicareStatus.Yes}
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.mbi}
        />
        <Input
          id="role-years"
          type="number"
          label="Role Years"
          onTextChange={(value) =>
            onChangeHandler('roleYears', typeof value !== 'number' ? null : value)
          }
          value={typeof data.roleYears === 'number' ? data.roleYears : ''}
          placeholder="Enter years"
          required={isGuide && typeof data.roleMonths !== 'number'}
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.roleYears}
        />
        <Input
          id="role-months"
          type="number"
          label="Role Months"
          onTextChange={(value) =>
            onChangeHandler('roleMonths', typeof value !== 'number' ? null : value)
          }
          value={typeof data.roleMonths === 'number' ? data.roleMonths : ''}
          placeholder="Enter months"
          required={isGuide && typeof data.roleYears !== 'number'}
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.roleMonths}
        />
        <Select
          id="preferred-language"
          label="Preferred Language"
          options={CaregiverPreferredLanguageOptions}
          onChange={(op) => onChangeHandler('preferredLanguage', op.value)}
          value={CaregiverPreferredLanguageOptions.find(
            (op) => op.value === data.preferredLanguage,
          )}
          placeholder="Select preferred language"
          required
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.preferredLanguage}
        />
        <Select
          id="tech-comfort-level"
          label="Tech Comfort Level"
          options={TechComfortLevelOptions}
          onChange={(op) => onChangeHandler('techComfortLevel', op.value)}
          value={TechComfortLevelOptions.find((op) => op.value === data.techComfortLevel)}
          placeholder="Select tech comfort level"
          required
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.techComfortLevel}
        />
      </div>
      {renderButtons({
        containerClassName: styles.buttonsContainer,
        onClose,
        onSubmit: onSubmitHandler,
        isSubmitEnabled: !Object.values(errors).some((e) => e),
        submitButtonText: 'Save and Close',
      })}
    </BasePopup>
  );
};

CaregiverPopup.propTypes = {
  patientId: PropTypes.number,
  caregiver: PropTypes.exact(PatientCaregiver.schema),
  hasActiveCaregivers: PropTypes.bool,
  isGuide: PropTypes.bool,
  onClose: PropTypes.func,
};

export default CaregiverPopup;
